import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyListModule } from '@angular/material/legacy-list';
import { MatLegacyPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLineModule } from '@angular/material/core';
import {
    FILTER_TYPE_SEARCH_MULTI_SELECT,
    FilterOption,
    IFilterTypeSearchMultiSelectValue,
} from '../../../app-filter-module/app-filter/app-filter.component';
import {
    CKEditorDefaultConfigEmailFontFamilyOptions,
    CKEditorDefaultConfigEmailFontSizeOptions, CKEditorDefaultConfigEmailTemplate,
} from '../../../../helper/ckeditor.helper';
import { TextTemplateTagEntity } from '../../../../dave-data-module/entities/text-template-tag.entity';
import { getTextTemplateTag } from '../../../../dave-data-module/State/selectors/text-template-tag.selector';
import {
    TextTemplateEntity
} from '../../../../dave-data-module/entities/text-template.entity';
import { FilterTypes } from '../../../../services/default-filter.service';
import { MatDialogConfig } from '@angular/material/dialog';
import { AppButtonModule } from '../../../app-button-module/app-button.module';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
    MAT_LEGACY_DIALOG_DATA,
    MatLegacyDialog,
    MatLegacyDialogModule,
    MatLegacyDialogRef,
} from '@angular/material/legacy-dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { CkEditorComponent } from '../../../../ck-editor/ck-editor.component';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { HintIconComponent } from '../../../../hint-icon/hint-icon.component';
import { MatLegacySlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { CustomFormsModule } from '../../../../custom-forms-module/custom-forms.module';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatBadgeModule } from '@angular/material/badge';
import { AppFilterModule } from '../../../app-filter-module/app-filter.module';
import { appMatDialogDefaultConfig } from '../../../../helper/helper';
import { TextTemplateActionTypes } from '../../../../dave-data-module/State/actions/text-template.actions';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { BaseActionTypes } from '../../../../dave-data-module/State/actions/base.actions';
import { TextTemplateTagActionTypes } from '../../../../dave-data-module/State/actions/text-template-tag.actions';
import { filter, map, shareReplay, skip, startWith, switchMap, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { LoadingService } from '../../../../services/loading.service';
import { TextTemplateTagResolver } from '../../../../dave-data-module/guards/text-template-tag.resolver';
import { Store } from '@ngrx/store';
import { AppDialogService } from '../../../app-dialog-module/app-dialog.service';
import {
    getTextTemplate,
} from '../../../../dave-data-module/State/selectors/text-template.selector';
import { State } from '../../../../dave-data-module/State';
import { TextareaModule } from '../../../../components/templates/textarea/textarea.module';
import { MatLegacyCardModule } from '@angular/material/legacy-card'
import { TextTemplateResolver } from '../../../../dave-data-module/guards/text-template.resolver';
import { marked } from 'marked';

export interface TemplateDialogComponentData {
     defaultFontValues$?: Observable<{size: CKEditorDefaultConfigEmailFontSizeOptions, family: CKEditorDefaultConfigEmailFontFamilyOptions}>;
    defaultTextArea?: boolean;
}

 interface TextTemplateListItem {
    template: TextTemplateEntity;
    tags: TextTemplateTagEntity[];
    tagNames: string;
}

interface TextTemplateForm {
    id: FormControl<number | null>;
    name: FormControl<string | null>;
    content: FormControl<string | null>;
    private: FormControl<boolean | null>;
    tags: FormControl<TextTemplateTagEntity[] | null>,
}

interface TemplateFilterValues {
    [FilterTypes.TemplateTag]: IFilterTypeSearchMultiSelectValue[];
}

export type TemplateDialogComponentDataReturnData = string | undefined;

export function pickTemplateLongTextDialog(dialog: MatLegacyDialog, onContent: (content: string) => void) {
    TemplateDialogComponent.open(dialog, { defaultTextArea: true })
        .pipe(filter(Boolean), take(1))
        .subscribe(onContent);
}

export function addTemplate(
    dialog: MatLegacyDialog,
    form: FormControl<string>,
    textarea?: HTMLTextAreaElement | null,
) {
    const hasCursor = !!textarea && document.activeElement === textarea;
    const start = hasCursor ? textarea!.selectionStart : null;
    const end = hasCursor ? textarea!.selectionEnd : null;

    TemplateDialogComponent.open(dialog, { defaultTextArea: true })
        .pipe(filter(Boolean), take(1))
        .subscribe((content) => {

            const current = form.value ?? '';

            if (start == null || end == null) {
                form.setValue(current + content);
                form.markAsDirty();
                return;
            }

            const s = Math.min(start, current.length);
            const e = Math.min(end, current.length);
            form.setValue(current.slice(0, s) + content + current.slice(e));
            form.markAsDirty();

            const newPos = s + content.length;
            setTimeout(() => {
                textarea!.focus();
                textarea!.setSelectionRange(newPos, newPos);
            });
        });
}
@Component({
  selector: 'app-template-dialog',
  standalone: true,
    imports: [CommonModule, AppButtonModule, FontAwesomeModule, MatDividerModule, MatLegacyListModule, MatLegacyPaginatorModule, MatLegacyTooltipModule, MatLineModule, CdkDrag, CdkDragHandle, MatLegacyDialogModule, ReactiveFormsModule, MatLegacyInputModule, CkEditorComponent, MatLegacySelectModule, HintIconComponent, MatLegacySlideToggleModule, CustomFormsModule, MatLegacyButtonModule, MatBadgeModule, AppFilterModule, TextareaModule, MatLegacyCardModule],
  templateUrl: './template-dialog.component.html',
  styleUrls: ['./template-dialog.component.scss']
})
export class TemplateDialogComponent {
    public static DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
        hasBackdrop: false,
        minWidth: '75vw',
        minHeight: '75vh',
    };
    public static open(
        dialog: MatLegacyDialog,
        data?: TemplateDialogComponentData,
    ): Observable<TemplateDialogComponentDataReturnData> {
        return dialog
            .open<TemplateDialogComponent, TemplateDialogComponentData, TemplateDialogComponentDataReturnData>(
                TemplateDialogComponent,
                { ...TemplateDialogComponent.DefaultConfig, data },
            )
            .afterClosed();
    }
    protected defaultFontValues$: Observable<{size: CKEditorDefaultConfigEmailFontSizeOptions, family: CKEditorDefaultConfigEmailFontFamilyOptions}>;
    protected defaultTextArea: boolean = false;
    protected textTemplateTags$ = this.store.select(getTextTemplateTag);
    protected tagMapFn = (tag: TextTemplateTagEntity): string => tag.Text;
    protected searchVisible = false;
    protected searchForm = new FormControl<string>('');
    @ViewChild('searchInput') searchInput: ElementRef;
    public form: FormGroup<TextTemplateForm> = new FormGroup<TextTemplateForm>({
        id: new FormControl<number>(null),
        name: new FormControl<string>('', {
            validators: Validators.required,
            nonNullable: true
        }),
        content: new FormControl<string>(null, Validators.required),
        private: new FormControl<boolean>(true, Validators.required),
        tags: new FormControl<TextTemplateTagEntity[] | null>(null),
    });

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        this.searchForm.setValue('');
        if (this.searchVisible) {
            setTimeout(() => {
                this.searchInput.nativeElement.focus();
            }, 0);
        }
    }

    deleteTemplate() {
        if(this.selectedTemplate?.template.Id){
            this.appDialog
                .OpenConfirmationDialog({paragraph: `Vorlage ${this.selectedTemplate.template.Name} wirklich löschen?`, styleDelete: true,})
                .subscribe(([result,]) => {
                    if (result) {
                        this.store.dispatch(TextTemplateActionTypes.Delete({ Payload: { Id: String(this.selectedTemplate.template.Id) } }));
                        this.selectedTemplate = null;
                    }
                });
        }else{
        this.selectedTemplate = null;
        }
    }

    createTemplate() {
        this.selectedTemplate = { template: new TextTemplateEntity(), tags: [], tagNames: '' };
        this.form.reset({ private: false, content: '' });
    }

    editTemplate() {
        this.LS.startLoading('save-text-template');
        this.store.dispatch(TextTemplateActionTypes.Change({ Payload: {
            Id: String(this.selectedTemplate?.template.Id),
            TagIds: this.form.controls.tags.value?.map(t => String(t.Id)) ?? [],
            Name: this.form.controls.name.value,
            Private: this.form.controls.private.value,
            Content: this.form.controls.content.value,
        } }));

        return firstValueFrom(this.actions$.pipe(ofType(TextTemplateActionTypes.UpdateOne, BaseActionTypes.ErrorAction))).then((action) => {
            this.LS.endLoading('save-text-template');
            if (action.type === TextTemplateActionTypes.UpdateOne.type) {
                this.form.markAsPristine();
                this.toastrService.success('Vorlage erfolgreich gespeichert');
            }
        });
    }

    createNewTemplate() {
        this.LS.startLoading('save-text-template');
        this.store.dispatch(TextTemplateActionTypes.CreateRequest({ Payload: {
            Name: this.form.controls.name.value,
            Private: this.form.controls.private.value,
            Content: this.form.controls.content.value,
            TagIds: this.form.controls.tags.value?.map(t => String(t.Id)) ?? [],
        } }));

        return firstValueFrom(this.actions$.pipe(ofType(TextTemplateActionTypes.CreateSuccess, BaseActionTypes.ErrorAction))).then((action) => {
            this.LS.endLoading('save-text-template');
            if (action.type === TextTemplateActionTypes.CreateSuccess.type) {
                const created = (action as ReturnType<typeof TextTemplateActionTypes.CreateSuccess>).Payload;
                this.form.controls.id.setValue(created.Id);
                this.selectedTemplate = { template: created, tags: this.form.controls.tags.value ?? [], tagNames: this.form.controls.tags.value?.map(t => t.Text).join(', ') ?? '' };
                this.form.markAsPristine();
                this.toastrService.success('Vorlage erfolgreich angelegt');
            }
        });
    }

    saveTemplate() {
        if (this.form.controls.id.value) {
            this.editTemplate();
        } else {
            this.createNewTemplate();
        }
    }

    selectedTemplate: TextTemplateListItem | null = null;
    textTemplateList$: Observable<TextTemplateListItem[]>;

    selectTemplate(item: TextTemplateListItem) {
        this.selectedTemplate = item;
        this.form.patchValue({
            id: item.template.Id,
            name: item.template.Name,
            content: item.template.Content,
            private: item.template.Private,
            tags: item.tags,
        });
        this.form.markAsPristine();
    }


    protected onTagsChange(tags: TextTemplateTagEntity[]) {
        const current = this.form.controls.tags.value ?? [];
        if (current.length === tags.length && current.every((t, i) => t.Id === tags[i]?.Id)) {
            return;
        }
        this.form.controls.tags.setValue(tags);
        this.form.controls.tags.markAsDirty();
    }

    protected convertToPlainText(content: string): string {
        const html = marked.parse(content ?? '', { async: false, breaks: true, gfm: true }) as string;
        const doc = new DOMParser().parseFromString(html, 'text/html');

        doc.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
        doc.querySelectorAll('li').forEach(li => li.prepend('- '));
        doc.querySelectorAll('th, td').forEach(cell => cell.append('\t'));
        doc.querySelectorAll('li, tr').forEach(el => el.append('\n'));
        doc.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, table, blockquote')
            .forEach(el => el.append('\n\n'));

        return (doc.body.textContent ?? '')
            .replace(/[ \t]+\n/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    }

    protected apply() {
        const content = this.form.controls.content.value ?? '';

        this.dialogRef.close(
            this.defaultTextArea
                ? this.convertToPlainText(content)
                : content
        );
    }

    protected onNewTag(text: string) {
        this.store.dispatch(TextTemplateTagActionTypes.CreateRequest({ Payload: { Text: text } }));
        this.form.controls.tags.disable();
        this.textTemplateTags$.pipe(skip(1), take(1)).subscribe(allTags => {
            this.form.controls.tags.enable();
            const newTag = allTags.find(t => t.Text === text);
            if (newTag) {
                this.form.controls.tags.setValue([...(this.form.controls.tags.value ?? []), newTag]);
                this.form.controls.tags.markAsDirty();
            }
        });
    }

    protected DeleteTag(Option: TextTemplateTagEntity, event: MouseEvent) {
        event.stopPropagation();
        this.appDialog
            .OpenConfirmationDialog({paragraph: `Tag ${Option.Text} wirklich löschen?`, styleDelete: true,})
            .subscribe(([result,]) => {
                if (result) {
                    this.store.dispatch(TextTemplateTagActionTypes.Delete({ Payload: { Id: Option.Id.toString() } }));
                }
            });
    }

    constructor(@Inject(MAT_LEGACY_DIALOG_DATA) dialogData: TemplateDialogComponentData,
                private dialogRef: MatLegacyDialogRef<TemplateDialogComponent, TemplateDialogComponentDataReturnData>,
                private toastrService: ToastrService,
                public LS: LoadingService,
                private actions$: Actions,
                textTemplateResolver: TextTemplateResolver,
                textTemplateTagResolver: TextTemplateTagResolver,
                private store: Store<State>,
                private appDialog: AppDialogService,
                ) {
        this.defaultFontValues$ = dialogData?.defaultFontValues$;
        this.defaultTextArea = dialogData?.defaultTextArea ?? false;
        textTemplateResolver.resolve();
        textTemplateTagResolver.resolve();
        this.textTemplateList$ = combineLatest([
            this.store.select(getTextTemplate),
            this.store.select(getTextTemplateTag),
            this.searchForm.valueChanges.pipe(startWith(''))
        ]).pipe(
            map(([templates, tags, search]) => {

                const searchTerm = (search || '').trim().toLowerCase();

                const mapped = templates.map(template => {
                    const templateTags = tags.filter(tag =>
                        template.TagIds?.includes(String(tag.Id))
                    );

                    return {
                        template,
                        tags: templateTags,
                        tagNames: templateTags.map(x => x.Text).join(', ')
                    };
                });

                if (!searchTerm) {
                    return mapped;
                }

                return mapped.filter(item =>
                    item.template.Name?.toLowerCase().includes(searchTerm) ||
                    item.template.Content?.toLowerCase().includes(searchTerm) ||
                    item.tagNames?.toLowerCase().includes(searchTerm)
                );
            }),
            switchMap((data) =>
                this.TemplateFilterValues$.pipe(
                    map((filterValues) =>
                        data.filter((d) =>

                            (!filterValues[FilterTypes.TemplateTag]?.length ||
                                filterValues[FilterTypes.TemplateTag].some((f) =>
                                    d.tags?.some((tag) => tag.Id === f.id)
                                )
                            )

                        )
                    )
                )
            ),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }



    protected onEditorContentChange($event: string) {
        if (this.form.controls.content.value === $event) {
            return;
        }
        this.form.controls.content.setValue($event);
        this.form.controls.content.markAsDirty();

    }

    protected TemplateFilterValues$: BehaviorSubject<TemplateFilterValues> = new BehaviorSubject({
        [FilterTypes.TemplateTag]: [],
    });

    protected TemplateFilterSettings: Observable<FilterOption<keyof TemplateFilterValues>[]> = combineLatest([this.textTemplateTags$]).pipe(
        map(([templateTags]) => {
            const filters: FilterOption<keyof TemplateFilterValues>[] = [];
            filters.push({
                Name: FilterTypes.TemplateTag,
                Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                Label: 'Kategorie',
                Values: templateTags.map<IFilterTypeSearchMultiSelectValue<number>>((templateTag) => ({
                    id: templateTag.Id,
                    label: templateTag.Text,
                })),
            });

            return filters;
        }),
    );

    public FilterAmount$ = this.TemplateFilterValues$.pipe(
        map((filter) => {
            const hasDateRange = (value: { from?: any; to?: any } | null | undefined) =>
                !!value && (value.from || value.to);

            let amount = 0;
            if (filter[FilterTypes.TemplateTag]?.length) {
                amount += 1;
            }
            return amount;
        }),
    );

    protected readonly CKEditorDefaultConfigEmailTemplate = CKEditorDefaultConfigEmailTemplate;


}
